import { type FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { getJson, mutateJson } from '../api/client';
import { Field, Input, Select } from '../components/form';
import { Alert, Button, Card, EmptyState, Spinner } from '../components/ui';
import { brl, dataBr, mesAno, parseReais, reaisParaCampo } from '../lib/format';
import styles from './screens.module.css';

interface Competencia {
  id: number;
  ano: number;
  mes: number;
}
type Momento = 'inicio' | 'fim';
interface Posicao {
  id: number;
  tipoFundo: string;
  momento: Momento;
  data: string | null;
  valorBruto: number | null;
  irIof: number;
  valorLiquido: number;
  documentoId: number | null;
}
interface Fundos {
  fundos: Posicao[];
  totais: { inicio: number; fim: number };
}

type Carga = { estado: 'carregando' } | { estado: 'erro' } | { estado: 'ok'; dados: Fundos };
type Aviso = { kind: 'success' | 'danger'; texto: string };
type Campo = 'bruto' | 'irIof' | 'liquido' | 'data';

export const FUNDOS: Record<string, string> = {
  reserva: 'Fundo de reserva',
  investimento: 'Investimento',
  pintura: 'Fundo de pintura',
  renda_fixa: 'Renda fixa',
  conta_corrente: 'Conta corrente',
};
const MOMENTOS: Record<Momento, string> = { inicio: 'Início', fim: 'Fim' };
const MSG_VALOR = 'Informe o valor em reais, como 1.234,56.';
const MSG_DATA = 'Informe a data no formato válido.';

/** Erros de validação da API que pertencem a um campo do formulário. */
const ERRO_DE_CAMPO: Record<string, { campo: Campo; mensagem: string }> = {
  invalid_valor_liquido: { campo: 'liquido', mensagem: MSG_VALOR },
  invalid_valor_bruto: { campo: 'bruto', mensagem: MSG_VALOR },
  invalid_ir_iof: { campo: 'irIof', mensagem: MSG_VALOR },
  invalid_data: { campo: 'data', mensagem: MSG_DATA },
};
/** Demais erros da API (código → mensagem), mostrados acima da tabela. */
const ERRO_GERAL: Record<string, string> = {
  tenant_suspended: 'Condomínio suspenso — somente leitura.',
  competencia_not_found: 'Competência não encontrada.',
  documento_not_found: 'Documento não encontrado.',
  invalid_tipo_fundo: 'Escolha o fundo e o momento.',
  invalid_momento: 'Escolha o fundo e o momento.',
};
const ERRO_GERAL_PADRAO = 'Não foi possível salvar. Tente novamente.';

/**
 * Posição dos fundos no início e no fim da competência (RF-A13): bruto, IR/IOF e líquido
 * por fundo, com o total líquido de cada momento. `editavel` só para o síndico de condomínio
 * ativo (POST é `requireRole('sindico')`; contábil e tenant suspenso, #127, só leem). Salvar
 * substitui a posição do mesmo fundo e momento; o total líquido vem somado do servidor.
 */
export function Fundos({ editavel }: { editavel: boolean }) {
  const [comps, setComps] = useState<Competencia[] | 'erro' | null>(null);
  const [sel, setSel] = useState('');
  const [carga, setCarga] = useState<Carga>({ estado: 'carregando' });
  const [aviso, setAviso] = useState<Aviso | null>(null);
  // Só o id: a posição em edição vem sempre da lista atual.
  const [editandoId, setEditandoId] = useState<number | null>(null);
  // Muda a cada salvamento para esvaziar o formulário de uma posição nova.
  const [rodada, setRodada] = useState(0);
  // Competência selecionada no momento: respostas de outra (troca rápida) são descartadas.
  const selAtual = useRef(sel);
  selAtual.current = sel;

  useEffect(() => {
    void getJson<{ competencias: Competencia[] }>('/api/competencias').then((d) => {
      if (!d) {
        setComps('erro');
        return;
      }
      setComps(d.competencias);
      if (d.competencias[0]) setSel(String(d.competencias[0].id));
    });
  }, []);

  const carregar = useCallback(async (competencia: string) => {
    const dados = await getJson<Fundos>(
      `/api/competencias/${encodeURIComponent(competencia)}/fundos`,
    );
    if (selAtual.current !== competencia) return;
    setCarga(dados ? { estado: 'ok', dados } : { estado: 'erro' });
  }, []);

  useEffect(() => {
    if (!sel) return;
    setCarga({ estado: 'carregando' });
    setEditandoId(null);
    void carregar(sel);
  }, [sel, carregar]);

  async function aoSalvar() {
    if (selAtual.current !== sel) return;
    setAviso({ kind: 'success', texto: 'Posição salva.' });
    setEditandoId(null);
    setRodada((r) => r + 1);
    await carregar(sel);
  }

  function aoFalhar(codigo?: string) {
    if (selAtual.current !== sel) return;
    setAviso({ kind: 'danger', texto: (codigo && ERRO_GERAL[codigo]) || ERRO_GERAL_PADRAO });
  }

  if (comps === null) return <Spinner />;
  if (comps === 'erro') {
    return <Alert kind="danger">Não foi possível carregar as posições dos fundos.</Alert>;
  }
  if (comps.length === 0) return <EmptyState>Nenhuma competência aberta.</EmptyState>;

  const dados = carga.estado === 'ok' ? carga.dados : null;
  const emEdicao = dados?.fundos.find((p) => p.id === editandoId) ?? null;

  return (
    <div className={styles.stack}>
      <Field label="Competência">
        {(id) => (
          <Select
            id={id}
            value={sel}
            onChange={(e) => {
              setAviso(null);
              setSel(e.target.value);
            }}
            style={{ maxWidth: '12rem' }}
          >
            {comps.map((c) => (
              <option key={c.id} value={c.id}>
                {mesAno(c)}
              </option>
            ))}
          </Select>
        )}
      </Field>

      {aviso ? <Alert kind={aviso.kind}>{aviso.texto}</Alert> : null}
      {carga.estado === 'carregando' ? <Spinner /> : null}
      {carga.estado === 'erro' ? (
        <Alert kind="danger">Não foi possível carregar as posições dos fundos.</Alert>
      ) : null}

      {editavel && dados ? (
        <PosicaoForm
          key={`${emEdicao?.id ?? 'nova'}-${rodada}`}
          competencia={sel}
          posicao={emEdicao}
          onSalvo={aoSalvar}
          onErroGeral={aoFalhar}
          onCancelar={() => setEditandoId(null)}
        />
      ) : null}

      {dados ? (
        dados.fundos.length === 0 ? (
          <EmptyState>Nenhuma posição registrada nesta competência.</EmptyState>
        ) : (
          <>
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Fundo</th>
                    <th>Momento</th>
                    <th>Data</th>
                    <th className={styles.num}>Bruto</th>
                    <th className={styles.num}>IR/IOF</th>
                    <th className={styles.num}>Líquido</th>
                    <th>Documento</th>
                    {editavel ? <th /> : null}
                  </tr>
                </thead>
                <tbody>
                  {dados.fundos.map((p) => (
                    <tr key={p.id}>
                      <th scope="row">{FUNDOS[p.tipoFundo] ?? p.tipoFundo}</th>
                      <td>{MOMENTOS[p.momento]}</td>
                      <td>{p.data ? dataBr(p.data) : '—'}</td>
                      <td className={styles.num}>
                        {p.valorBruto !== null ? brl(p.valorBruto) : '—'}
                      </td>
                      <td className={styles.num}>{brl(p.irIof)}</td>
                      <td className={styles.num}>{brl(p.valorLiquido)}</td>
                      <td>
                        {p.documentoId !== null ? (
                          <a
                            href={`/api/documentos/${p.documentoId}/download`}
                            target="_blank"
                            rel="noreferrer"
                            aria-label="Ver documento da posição"
                          >
                            Ver documento
                          </a>
                        ) : (
                          '—'
                        )}
                      </td>
                      {editavel ? (
                        <td>
                          <Button
                            size="sm"
                            variant="secondary"
                            aria-label={`Editar posição ${FUNDOS[p.tipoFundo] ?? p.tipoFundo} ${MOMENTOS[p.momento]}`}
                            onClick={() => {
                              setAviso(null);
                              setEditandoId(p.id);
                            }}
                          >
                            Editar
                          </Button>
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p>Total líquido no início: {brl(dados.totais.inicio)}</p>
            <p>Total líquido no fim: {brl(dados.totais.fim)}</p>
          </>
        )
      ) : null}
    </div>
  );
}

/** Registro ou edição da posição de um fundo; erros de validação vão para o campo. */
function PosicaoForm({
  competencia,
  posicao,
  onSalvo,
  onErroGeral,
  onCancelar,
}: {
  competencia: string;
  posicao: Posicao | null;
  onSalvo: () => Promise<void>;
  /** Erro da API fora dos campos (código; ausente = falha de rede). */
  onErroGeral: (codigo?: string) => void;
  onCancelar: () => void;
}) {
  const [fundo, setFundo] = useState(posicao?.tipoFundo ?? 'reserva');
  const [momento, setMomento] = useState<Momento>(posicao?.momento ?? 'inicio');
  const [data, setData] = useState(posicao?.data ?? '');
  const [bruto, setBruto] = useState(
    posicao?.valorBruto != null ? reaisParaCampo(posicao.valorBruto) : '',
  );
  const [irIof, setIrIof] = useState(
    posicao && posicao.irIof > 0 ? reaisParaCampo(posicao.irIof) : '',
  );
  const [liquido, setLiquido] = useState(posicao ? reaisParaCampo(posicao.valorLiquido) : '');
  const [erros, setErros] = useState<Partial<Record<Campo, string>>>({});
  const [enviando, setEnviando] = useState(false);

  async function enviar(e: FormEvent) {
    e.preventDefault();
    if (enviando) return;
    const centavosLiquido = parseReais(liquido);
    const centavosBruto = bruto.trim() ? parseReais(bruto) : undefined;
    const centavosIr = irIof.trim() ? parseReais(irIof) : undefined;
    const locais: Partial<Record<Campo, string>> = {};
    if (centavosLiquido === null) locais.liquido = MSG_VALOR;
    if (centavosBruto === null) locais.bruto = MSG_VALOR;
    if (centavosIr === null) locais.irIof = MSG_VALOR;
    setErros(locais);
    if (centavosLiquido === null || centavosBruto === null || centavosIr === null) return;

    setEnviando(true);
    const corpo = {
      tipoFundo: fundo,
      momento,
      ...(data ? { data } : {}),
      ...(centavosBruto !== undefined ? { valorBruto: centavosBruto } : {}),
      ...(centavosIr !== undefined ? { irIof: centavosIr } : {}),
      ...(posicao?.documentoId != null ? { documentoId: posicao.documentoId } : {}),
      valorLiquido: centavosLiquido,
    };
    const res = await mutateJson<{ error?: string }>(
      `/api/competencias/${encodeURIComponent(competencia)}/fundos`,
      corpo,
    );
    setEnviando(false);
    if (res.ok) {
      await onSalvo();
      return;
    }
    const codigo = res.data?.error;
    const deCampo = codigo ? ERRO_DE_CAMPO[codigo] : undefined;
    if (deCampo) setErros({ [deCampo.campo]: deCampo.mensagem });
    else onErroGeral(codigo);
  }

  return (
    <Card title={posicao ? 'Editar posição' : 'Registrar posição'}>
      <form onSubmit={enviar} noValidate>
        <div className={styles.row}>
          <Field label="Fundo">
            {(id) => (
              <Select
                id={id}
                value={fundo}
                disabled={posicao !== null}
                onChange={(e) => setFundo(e.target.value)}
              >
                {Object.entries(FUNDOS).map(([valor, rotulo]) => (
                  <option key={valor} value={valor}>
                    {rotulo}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label="Momento">
            {(id) => (
              <Select
                id={id}
                value={momento}
                disabled={posicao !== null}
                onChange={(e) => setMomento(e.target.value as Momento)}
              >
                <option value="inicio">Início</option>
                <option value="fim">Fim</option>
              </Select>
            )}
          </Field>
          <Field label="Data" error={erros.data}>
            {(id) => (
              <Input id={id} type="date" value={data} onChange={(e) => setData(e.target.value)} />
            )}
          </Field>
        </div>
        <div className={styles.row}>
          <Field label="Valor bruto (opcional)" error={erros.bruto}>
            {(id) => (
              <Input
                id={id}
                value={bruto}
                inputMode="decimal"
                placeholder="0,00"
                onChange={(e) => setBruto(e.target.value)}
                style={{ maxWidth: '10rem' }}
              />
            )}
          </Field>
          <Field label="IR/IOF (opcional)" error={erros.irIof}>
            {(id) => (
              <Input
                id={id}
                value={irIof}
                inputMode="decimal"
                placeholder="0,00"
                onChange={(e) => setIrIof(e.target.value)}
                style={{ maxWidth: '10rem' }}
              />
            )}
          </Field>
          <Field label="Valor líquido" error={erros.liquido}>
            {(id) => (
              <Input
                id={id}
                value={liquido}
                inputMode="decimal"
                placeholder="0,00"
                onChange={(e) => setLiquido(e.target.value)}
                style={{ maxWidth: '10rem' }}
              />
            )}
          </Field>
        </div>
        <p className="muted">Salvar substitui a posição já registrada do mesmo fundo e momento.</p>
        <div className={styles.row}>
          <Button type="submit" disabled={enviando}>
            Salvar posição
          </Button>
          {posicao ? (
            <Button variant="ghost" onClick={onCancelar}>
              Cancelar
            </Button>
          ) : null}
        </div>
      </form>
    </Card>
  );
}
